/**
 * CategoryChip / TagChip — UX §3.8, §3.9.
 * Pill, hairline border, px-3 py-1, text-xs uppercase tracking-wider.
 * Optional Phosphor icon left of label.
 *   active: bg accent, text bg, no border
 *   default: hairline ink-200, text ink-700
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface ChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  active?: boolean;
  /** Optional icon node (Phosphor regular weight only). */
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const BASE = cn(
  'inline-flex items-center gap-1.5',
  'px-3 py-1',
  'font-sans text-xs uppercase tracking-[0.12em]',
  'border rounded-full',
  'transition-colors duration-200 ease-out-slow',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
  'disabled:opacity-40 disabled:pointer-events-none',
);

export const CategoryChip = React.forwardRef<HTMLButtonElement, ChipProps>(
  function CategoryChip({ active, icon, children, className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        role="button"
        aria-pressed={Boolean(active)}
        className={cn(
          BASE,
          active
            ? 'bg-ink-900 text-bg border-ink-900'
            : 'bg-transparent text-ink-700 border-ink-200 hover:bg-surface-2',
          className,
        )}
        {...rest}
      >
        {icon ? (
          <span aria-hidden className="[&_svg]:h-3.5 [&_svg]:w-3.5">
            {icon}
          </span>
        ) : null}
        <span>{children}</span>
      </button>
    );
  },
);
